import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { isSocialPeriodLocked } from '../promote/socialPeriod';
import { FANS_PERIODS, FansPeriodDays } from './useFansPeriod';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansPeriodPickerProps {
  days: FansPeriodDays;
  /** The history window of the plan in days. A longer period is disabled (design c6). */
  historyDaysLimit: number;
  onChange: (days: FansPeriodDays) => void;
}

/** The period picker of the Fans screen: 7d, 28d, 90d, 1y (design presave-subscriptions c1). */
const FansPeriodPickerComponent: React.FC<FansPeriodPickerProps> = ({ days, historyDaysLimit, onChange }) => {
  const intl = useIntl();

  const options = useMemo(
    () =>
      FANS_PERIODS.map((option) => {
        const isLocked = isSocialPeriodLocked(option, historyDaysLimit);
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === days}
            disabled={isLocked}
            title={isLocked ? intl.formatMessage(messages.periodLocked, { days: historyDaysLimit }) : undefined}
            className={clsx(option === days && styles.periodOn)}
            onClick={() => onChange(option)}
          >
            {option === 365
              ? intl.formatMessage(messages.periodYear)
              : intl.formatMessage(messages.periodDays, { days: option })}
          </button>
        );
      }),
    [days, historyDaysLimit, intl, onChange]
  );

  return (
    <div className={styles.period} role="radiogroup" aria-label={intl.formatMessage(messages.periodLabel)}>
      {options}
    </div>
  );
};

export const FansPeriodPicker = TypedMemo(FansPeriodPickerComponent);
